import React from 'react';
import CurrencyInput from 'react-currency-input-field';
import PhoneInput from 'react-phone-number-input';

import { lang, remCss, rgba, scssVariables } from 'src/helpers';
import constants from 'src/constants';
import { MCButton } from './MCButton';
import SelectField from './SelectField';
import { BASE_ICONS } from '../svg';
import FileBox from './FileBox';
import LANG_CONST from 'src/helpers/lang/const';
import { MCError } from './MCText';

const guessAutoComplete = (name = '', type = '') => {
  const n = name.toLowerCase();
  const t = type.toLowerCase();

  if (t === 'email' || n.includes('email')) return 'email';
  if (
    t === 'tel' ||
    t === 'phone' ||
    n.includes('phone') ||
    n.includes('mobile') ||
    n.includes('tel')
  )
    return 'tel';
  if (t === 'password') {
    if (n.includes('new') || n.includes('confirm') || n.includes('repeat') || n === 'password2')
      return 'new-password';
    return 'current-password';
  }
  if (n === 'username' || n.includes('username') || n.includes('urlname')) return 'username';
  if (n.includes('firstname') || n === 'givenname' || n.includes('given_name')) return 'given-name';
  if (
    n.includes('lastname') ||
    n === 'familyname' ||
    n.includes('family_name') ||
    n.includes('surname')
  )
    return 'family-name';
  if (n === 'name' || n === 'fullname' || n.includes('full_name')) return 'name';
  if (n.includes('street') || n === 'address1' || n === 'addressline1') return 'address-line1';
  if (n === 'address2' || n === 'addressline2') return 'address-line2';
  if (n.includes('city') || n.includes('cityaddress')) return 'address-level2';
  if (n.includes('state') || n.includes('stateaddress')) return 'address-level1';
  if (n.includes('country') || n.includes('countryaddress')) return 'country-name';
  if (n.includes('zip') || n.includes('postal') || n.includes('postcode')) return 'postal-code';
  if (n.includes('birthday') || n.includes('birthdate') || n.includes('dob')) return 'bday';
  if (n.includes('gender') || n.includes('sex')) return 'sex';
  if (n.includes('org') || n.includes('company') || n.includes('clinic')) return 'organization';
  if (n.includes('url') || n.includes('website')) return 'url';
  if (n.includes('avatar') || n.includes('photo') || n.includes('picture')) return 'photo';

  return 'on';
};

class TextField extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showPassword: false,
      resolvedAutoComplete: props.autoComplete ?? guessAutoComplete(props.name, props.type ?? '')
    };
  }

  componentDidUpdate(prevProps) {
    const { autoComplete, name, type } = this.props;
    if (
      prevProps.autoComplete !== autoComplete ||
      prevProps.name !== name ||
      prevProps.type !== type
    ) {
      this.setState({
        resolvedAutoComplete: autoComplete ?? guessAutoComplete(name, type ?? '')
      });
    }
  }
  onChangedPhone = (v) => {
    const { name, onChange } = this.props;
    onChange(name, v || '');
  };
  onChanged = (e) => {
    const { type, name, onChange } = this.props;

    if (type === 'number') {
      this.onChangedNumber(e);
    } else {
      onChange(name, e.target.value, e);
    }
  };

  onChangedNumber = (e) => {
    const { name, onChange } = this.props;
    const value = e.target.value?.split(/[$,%]/).join('');

    onChange(
      name,
      value !== '' && !isNaN(value)
        ? value.toString().slice(-1) !== '.'
          ? parseFloat(value)
          : value
        : ''
    );
  };

  onChangedMoney = (value) => {
    this.onChangedNumber({
      target: {
        value: value || ''
      }
    });
  };

  onBlur = (e) => {
    const { name, onBlur } = this.props;
    if (onBlur) onBlur(name, e.target.value);
  };

  render() {
    let {
      label,
      labelHtml,
      template = 6,
      type,
      value,
      name,
      placeholder = '',
      currency = null,
      invalid,
      requiredErrorMessage = null,
      errorName = null,
      readOnly,
      onChange,
      onRemove = null,
      autoFocus = false,
      prefixEl = null,
      suffixEl = null,
      description = null,
      disabled = false,
      multiple = false,
      inputProps = {},
      inputTestId,
      defaultCountryName,
      defaultCountry,
      accept,
      files,
      thumbnail,
      isOptional = false,
      ...other
    } = this.props;

    if (defaultCountryName) {
      defaultCountry = constants.general.country_code_by_name[defaultCountryName];
    }

    const { resolvedAutoComplete } = this.state;
    const testId = inputTestId || (name ? `input-${name}` : undefined);

    let inputEl;

    switch (type) {
      case 'select':
        inputEl = (
          <SelectField
            template='6'
            isClearable={false}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            invalid={invalid}
            {...inputProps}
          />
        );
        break;
      case 'phone':
        inputEl = (
          <PhoneInput
            international
            defaultCountry={defaultCountry}
            data-testid={testId}
            autoComplete={resolvedAutoComplete}
            autoFocus={autoFocus}
            readOnly={readOnly}
            name={name}
            placeholder={lang.t(placeholder)}
            disabled={disabled}
            id={'mc-' + name}
            className={`mc-input-box ${invalid && invalid[name] ? 'invalid' : 'valid'}`}
            value={value}
            onChange={this.onChangedPhone}
            onBlur={this.onBlur}
          />
        );
        break;
      case 'money':
        inputEl = (
          <CurrencyInput
            decimalSeparator='.'
            groupSeparator=','
            data-testid={testId}
            // prefix={!currency ? '$' : null}
            // suffix={currency}
            autoComplete={resolvedAutoComplete}
            autoFocus={autoFocus}
            readOnly={readOnly}
            name={name}
            placeholder={lang.t(placeholder)}
            id={'mc-' + name}
            className={`mc-input-box ${invalid && invalid[name] ? 'invalid' : 'valid'}`}
            value={value}
            onValueChange={this.onChangedMoney}
            onBlur={this.onBlur}
          />
        );
        if (currency && !suffixEl) {
          suffixEl = <span className={'mc-input-currency'}>{currency}</span>;
        }
        break;
      case 'password':
        inputEl = (
          <>
            <input
              data-testid={testId}
              autoComplete={resolvedAutoComplete}
              autoFocus={autoFocus}
              readOnly={readOnly}
              type={this.state.showPassword ? 'text' : type}
              name={name}
              placeholder={lang.t(placeholder)}
              disabled={disabled}
              id={'mc-' + name}
              className={`mc-input-box ${invalid && invalid[name] ? 'invalid' : 'valid'}`}
              value={value}
              onChange={this.onChanged}
              onBlur={this.onBlur}
            />
            <MCButton
              className={'mc-password-icon'}
              style={{ paddingRight: remCss(5) }}
              fill={rgba(scssVariables['colors-mc-text-primary'], 0.4)}
              onClick={() => this.setState({ showPassword: !this.state.showPassword })}
              iconname={this.state.showPassword ? BASE_ICONS.EYE_FILL : BASE_ICONS.EYE_FILL_SLASH}
            />
          </>
        );
        break;
      default:
        inputEl = (
          <input
            data-testid={testId}
            autoComplete={resolvedAutoComplete}
            autoFocus={autoFocus}
            readOnly={readOnly}
            type={type === 'number' ? 'text' : type}
            name={name}
            placeholder={lang.t(placeholder)}
            disabled={disabled}
            id={'mc-' + name}
            accept={accept}
            className={`mc-input-box ${invalid && invalid[name] ? 'invalid' : 'valid'}`}
            value={value}
            onChange={this.onChanged}
            onBlur={this.onBlur}
            multiple={multiple}
            {...inputProps}
          />
        );
        break;
    }

    let error = invalid && invalid[name];
    if (error?.includes('required')) {
      if (requiredErrorMessage) {
        error = lang.t(requiredErrorMessage);
      } else {
        errorName = errorName || label || name || placeholder || 'Field';
        error = lang.t(LANG_CONST.validation.required, { label: lang.t(errorName) });
      }
    }

    return (
      <div
        className={`mc-text-field-group template-${template} ${disabled ? 'disabled' : ''}`}
        {...other}
      >
        {labelHtml && <label htmlFor={'mc-' + name}>{labelHtml}</label>}
        {label && (
          <label htmlFor={'mc-' + name}>
            {lang.t(label)}{' '}
            {isOptional && <span className={'mc-label-414'}>({lang.t(LANG_CONST.optional)})</span>}
          </label>
        )}
        <div className={'mc-input-box-cover'} style={{ position: 'relative' }}>
          {prefixEl}
          {inputEl}
          {type === 'file' && (
            <>
              <MCButton
                type={'button'}
                onClick={() => document.getElementById('mc-' + name).click()}
                label={'Choose File'}
                primary
                light
                large
                style={{ margin: remCss(8) }}
              />
              <span className={'mc-input-file-label mc-flex'}>
                {!files
                  ? lang.t('No file Chosen')
                  : (multiple ? files : [files]).map((f, i) => (
                      <FileBox
                        key={'mc-media-' + i}
                        file={f}
                        onClose={onRemove ? () => onRemove(f) : null}
                        styles={{ width: remCss(50), height: remCss(50) }}
                        onlyPreview={true}
                        showFileName={false}
                        thumbnail={thumbnail}
                      />
                    ))}
              </span>
            </>
          )}
          {suffixEl}
        </div>
        {!!description && <label>{description}</label>}
        {error && <MCError error={error} label={label || errorName} />}
      </div>
    );
  }
}

export default TextField;
